import { isReservedAcademySlug } from '@court-sync/shared';

export type AcademyHostMode = 'path' | 'subdomain';

export interface AcademyHostContext {
  mode: AcademyHostMode;
  /** Subdomain label from hostname (subdomain mode only). */
  subdomainLabel?: string;
}

const PLATFORM_HOSTS = new Set([
  'localhost',
  'app.courtsync.io',
  'app.dev.courtsync.io',
  'courtsync.io',
  'www.courtsync.io',
  'dev.courtsync.io',
]);

function isFirebaseHostingHost(hostname: string): boolean {
  return hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com');
}

/**
 * Resolve whether the current host is path-based (app.courtsync.io/academy/:slug)
 * or subdomain-based (elite.courtsync.io).
 */
export function getAcademyHostContext(hostname?: string): AcademyHostContext {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();

  if (!host || PLATFORM_HOSTS.has(host) || isFirebaseHostingHost(host)) {
    return { mode: 'path' };
  }

  // elite.localhost
  if (host.endsWith('.localhost')) {
    const label = host.split('.')[0];
    if (!label || isReservedAcademySlug(label)) {
      return { mode: 'path' };
    }
    return { mode: 'subdomain', subdomainLabel: label };
  }

  // elite.dev.courtsync.io
  if (host.endsWith('.dev.courtsync.io')) {
    const label = host.replace(/\.dev\.courtsync\.io$/, '');
    if (!label || label.includes('.') || isReservedAcademySlug(label)) {
      return { mode: 'path' };
    }
    return { mode: 'subdomain', subdomainLabel: label };
  }

  // elite.courtsync.io
  if (host.endsWith('.courtsync.io')) {
    const label = host.replace(/\.courtsync\.io$/, '');
    if (!label || label.includes('.') || isReservedAcademySlug(label)) {
      return { mode: 'path' };
    }
    return { mode: 'subdomain', subdomainLabel: label };
  }

  return { mode: 'path' };
}

export function isAcademySubdomainHost(hostname?: string): boolean {
  return getAcademyHostContext(hostname).mode === 'subdomain';
}
